import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { PenLine, Flag, ScrollText, CheckCircle2 } from 'lucide-react';
import { useApi } from '@/contexts/AuthContext';

const C = { navy: '#0D1B2A', slate: '#5C6B7A', green: '#1B7A3D', amber: '#8A6D1D', red: '#B0413E', line: '#E4E9EF' };

function SigRow({ item, api, onDone }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const act = async (action) => {
    if (action === 'flag' && note.trim().length < 5) return toast.error('A flag needs a note (min 5 chars)');
    setBusy(true);
    try {
      const r = await api.post(`/signatures/${item.sig_id}/${action}`, { note: note.trim() || null });
      toast.success(`${item.policy_number} ${r.data.status === 'counter_signed' ? 'counter-signed' : 'flagged for review'}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
    setBusy(false);
  };

  return (
    <div className="px-4 py-3 border-b last:border-b-0 flex flex-wrap items-center gap-x-4 gap-y-2" style={{ borderColor: C.line }} data-testid={`sig-row-${item.sig_id}`}>
      <div className="min-w-[220px]">
        <div className="text-sm font-semibold" style={{ color: C.navy }}>{item.business_name || '—'}</div>
        <div className="text-[10px] font-mono" style={{ color: C.slate }}>{item.policy_number}</div>
      </div>
      <div className="text-xs" style={{ color: C.slate }}>{item.class}</div>
      <div className="text-xs font-mono" style={{ color: C.navy }}>${(item.annual_premium || 0).toLocaleString()}</div>
      <span className="text-[10px] font-semibold px-2 py-0.5 rounded" style={{ background: '#EAF1FB', color: '#2A6FC4' }}>{item.bound_by}</span>
      <div className="text-[10px]" style={{ color: C.slate }}>{(item.bound_at || item.created_at || '').slice(0, 16).replace('T', ' ')}</div>
      <div className="ml-auto flex items-center gap-2">
        <input value={note} onChange={e => setNote(e.target.value)} placeholder="minute note (optional; required to flag)"
          className="w-64 border rounded-sm px-2 py-1 text-[11px]" style={{ borderColor: C.line }} data-testid={`sig-note-${item.sig_id}`} />
        <button onClick={() => act('sign')} disabled={busy}
          className="flex items-center gap-1 px-2.5 py-1.5 rounded text-[11px] font-semibold text-white disabled:opacity-50"
          style={{ background: C.green }} data-testid={`sig-sign-${item.sig_id}`}>
          <PenLine size={11} /> Counter-sign
        </button>
        <button onClick={() => act('flag')} disabled={busy}
          className="flex items-center gap-1 px-2.5 py-1.5 rounded text-[11px] font-semibold border disabled:opacity-50"
          style={{ borderColor: C.red, color: C.red }} data-testid={`sig-flag-${item.sig_id}`}>
          <Flag size={11} /> Flag
        </button>
      </div>
    </div>
  );
}

export default function SignatureQueuePage() {
  const api = useApi();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [minutes, setMinutes] = useState([]);
  const [showMinutes, setShowMinutes] = useState(false);

  const load = useCallback(async () => {
    try {
      const [q, s, m] = await Promise.all([
        api.get('/signatures/queue'), api.get('/signatures/stats'), api.get('/signatures/minutes?limit=40')]);
      setItems(q.data.items); setStats(s.data); setMinutes(m.data.minutes);
    } catch { /* noop */ }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="signature-queue-page">
      <div className="flex flex-wrap items-center gap-3">
        {stats && (
          <>
            <span className="text-xs px-2.5 py-1 rounded font-semibold" style={{ background: '#FDF6E3', color: C.amber }} data-testid="sig-stat-pending">{stats.pending} awaiting signature</span>
            <span className="text-xs px-2.5 py-1 rounded font-semibold" style={{ background: '#E8F4EC', color: C.green }} data-testid="sig-stat-signed">{stats.counter_signed} counter-signed</span>
            <span className="text-xs px-2.5 py-1 rounded font-semibold" style={{ background: '#FBEBEA', color: C.red }} data-testid="sig-stat-flagged">{stats.flagged} flagged</span>
          </>
        )}
        <button onClick={() => setShowMinutes(!showMinutes)}
          className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border"
          style={{ borderColor: C.line, color: C.navy }} data-testid="toggle-minute-log-btn">
          <ScrollText size={13} /> {showMinutes ? 'Hide minute log' : 'Minute log'}
        </button>
      </div>
      <div className="text-xs" style={{ color: C.slate }}>
        Every bound decision queues here for human counter-signature — binds are never blocked; this is the attestation trail. Flagging requires a minute note. Simulation-book policies are excluded.
      </div>

      {showMinutes && (
        <div className="rounded-lg border overflow-hidden" style={{ borderColor: C.line, background: '#fff' }} data-testid="minute-log">
          <div className="px-4 py-2 text-[10px] uppercase font-bold tracking-wider border-b" style={{ color: C.slate, borderColor: C.line }}>Minute log (append-only)</div>
          {minutes.length === 0 && <div className="p-4 text-xs" style={{ color: C.slate }}>No minutes yet.</div>}
          {minutes.map(m => (
            <div key={m.minute_id} className="px-4 py-2 border-b last:border-b-0 flex flex-wrap gap-x-3 text-[11px]" style={{ borderColor: C.line }} data-testid={`minute-${m.minute_id}`}>
              <span className="font-mono" style={{ color: C.slate }}>{m.at.slice(0, 16).replace('T', ' ')}</span>
              <span className="font-bold uppercase" style={{ color: m.action === 'counter_signed' ? C.green : m.action === 'flagged' ? C.red : C.slate }}>{m.action.replace('_', ' ')}</span>
              <span style={{ color: C.navy }}>{m.policy_number}</span>
              <span style={{ color: C.slate }}>{m.business_name}</span>
              <span style={{ color: C.slate }}>by {m.actor}</span>
              {m.note && <span className="italic" style={{ color: C.slate }}>— {m.note}</span>}
            </div>
          ))}
        </div>
      )}

      <div className="rounded-lg border overflow-hidden" style={{ borderColor: C.line, background: '#fff' }}>
        {items.length === 0 && (
          <div className="p-8 text-center text-sm" style={{ color: C.slate }} data-testid="sig-queue-empty">
            <CheckCircle2 size={26} className="mx-auto mb-2 opacity-40" />
            Nothing awaiting counter-signature — new binds appear here as they happen.
          </div>
        )}
        {items.map(i => <SigRow key={i.sig_id} item={i} api={api} onDone={load} />)}
      </div>
    </div>
  );
}
